import { motion } from "framer-motion";
import { Bot, ShieldCheck, Flame, LayoutTemplate, CalendarDays, Stamp } from "lucide-react";

const features = [
  {
    icon: Bot,
    title: "A friendly AI team",
    body: "Riya designs scroll-stopping creatives, Arjun writes copy and manages budgets, Meera stress-tests every ad before you see it.",
    span: "md:col-span-7",
  },
  {
    icon: ShieldCheck,
    title: "Guardrailed budgets",
    body: "Set spend limits once. The AI can never exceed them — every action stays inside your rules.",
    span: "md:col-span-5",
  },
  {
    icon: Flame,
    title: "Lead quality scoring",
    body: "Every lead scored Hot / Warm / Cold so your sales team calls the right people first.",
    span: "md:col-span-5",
  },
  {
    icon: LayoutTemplate,
    title: "Real ad previews",
    body: "Pixel-faithful feed mockups of every AI creative before it ships to any platform.",
    span: "md:col-span-7",
  },
  {
    icon: Stamp,
    title: "Brand Kit stamping",
    body: "AI never draws your logo. Your real logo file is composited pixel-perfect onto every creative.",
    span: "md:col-span-7",
  },
  {
    icon: CalendarDays,
    title: "Weekly creative calendar",
    body: "A 7-day creative plan across campaigns. Edit any day, or let it run on schedule.",
    span: "md:col-span-5",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.1 },
  }),
};

export default function FeaturesGrid() {
  return (
    <section id="features" className="py-24 md:py-32" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div custom={0} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">03 — The platform DNA</p>
          <h2 className="font-heading font-medium tracking-tight text-3xl md:text-4xl text-neutral-900 mt-4 max-w-2xl">
            Everything an ads team does — automated, audited, approved.
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-12 gap-6 md:gap-8 mt-14">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-40px" }}
              className={`${f.span} bg-white border border-neutral-200 rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:border-neutral-300 transition-colors duration-300`}
              data-testid={`feature-card-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[#FF4D00]/8 border border-[#FF4D00]/15 flex items-center justify-center">
                <f.icon size={20} className="text-[#FF4D00]" />
              </div>
              <h3 className="font-heading text-lg md:text-xl font-medium text-neutral-900 mt-6">{f.title}</h3>
              <p className="text-neutral-600 leading-relaxed mt-2.5 text-sm md:text-base">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
